import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Icon,
  InputAdornment,
  Paper,
  Switch,
  TextField
} from '@mui/material';

import { Field, useFormikContext } from 'formik';
import { round } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useJobmarketCompanyQuery } from 'api/hooks/jobmarket/companies';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { Company } from 'types/AppTypes';

import { InvoiceFormValues } from '../validation';

interface Props {
  companyId: number;
}

export default function InvoiceSettings({ companyId }: Props) {
  const { values, setFieldValue } = useFormikContext<InvoiceFormValues>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: company, isLoading, isError } = useJobmarketCompanyQuery(companyId);

  const amount = values.selectedPeriods.reduce((sum, p) => sum + p.price, 0);
  const taxAmount = round(amount * 0.19, 2);
  const finalAmount = values.invoiceSettings.tax ? amount + taxAmount : amount;

  const companyInvoiceAddress = (company: Company) => {
    return (
      <FormControl className="invoice-detail-modal__body__input" required fullWidth>
        <Grid container>
          <Grid item xs={4}>
            <FormLabel className="invoice-detail-modal__body__input__label">
              {t(translations.pages.invoices.invoiceAddress())}
            </FormLabel>
            {!values.customer.useCompanyAsCustomer ? (
              <Grid>
                <div>{values.customer.customer.companyName}</div>
                <div>{values.customer.customer.address}</div>
                <div>
                  {values.customer.customer.zip}, {values.customer.customer.city}
                </div>
                <div>{values.customer.customer.country}</div>
              </Grid>
            ) : company.sameInvoiceaddress ? (
              <Grid>
                <div>{company.name}</div>
                <div>{company.street}</div>
                <div>
                  {company.zip}, {company.city}
                </div>
                <div>{company.country}</div>
              </Grid>
            ) : (
              <Grid>
                <div>{company.invoiceName}</div>
                <div>{company.invoiceStreet}</div>
                <div>
                  {company.invoiceZip}, {company.invoiceCity}
                </div>
                <div>{company.invoiceCountry}</div>
              </Grid>
            )}
          </Grid>
          <Grid item xs={4}>
            <FormLabel
              className="invoice-detail-modal__body__input__label"
              style={{ marginTop: '1em' }}
            >
              {t(translations.pages.invoices.invoiceEmail())}
            </FormLabel>
            {!values.customer.useCompanyAsCustomer ? (
              <div>{values.customer.customer.email || ' ---'}</div>
            ) : company.sameInvoiceaddress ? (
              <div>{company.email || ' ---'}</div>
            ) : (
              <div>{company.invoiceEmail || ' ---'}</div>
            )}
          </Grid>
          <Grid item xs={4}>
            <FormLabel
              className="invoice-detail-modal__body__input__label"
              style={{ marginTop: '1em' }}
            >
              {t(translations.pages.invoices.taxInformation())}
            </FormLabel>
            {values.customer.useCompanyAsCustomer ? (
              <>
                <div>
                  <b>{t(translations.pages.invoices.taxId()) + ': '}</b>
                  {company.taxId ? company.taxId : ' ---'}
                </div>
                <div>
                  <b>{t(translations.pages.invoices.taxNumber()) + ': '}</b>
                  {company.taxNumber ? company.taxNumber : ' ---'}
                </div>
              </>
            ) : (
              <>
                <div>
                  <b>{t(translations.pages.invoices.taxId()) + ': '}</b>
                  {values.customer.customer.ustId || ' ---'}
                </div>
                <div>
                  <b>{t(translations.pages.invoices.taxNumber()) + ': '}</b>
                  {' ---'}
                </div>
              </>
            )}
          </Grid>
        </Grid>
      </FormControl>
    );
  };

  if (isError) {
    dispatch(AlertActions.error(`${t(translations.pages.companyDetailPage.errorLoading())}`));
    navigate('/', { replace: true });
    return null;
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Grid container>
      <Grid container>
        <Grid item xs={12}>
          {companyInvoiceAddress(company!)}
        </Grid>
      </Grid>
      <Grid container component={Paper} className="invoice-detail-modal__body__summary">
        <Grid container className="invoice-detail-modal__body__summary__heading">
          <Grid item xs={8}>
            {t(translations.pages.jobOffers.jobOffer())}
          </Grid>
          <Grid item xs={4} style={{ textAlign: 'end' }}>
            {t(translations.pages.invoices.periodId())} |{' '}
            {t(translations.pages.manageJobmarketSettings.price())}
          </Grid>
        </Grid>
        <Grid container>
          {values.selectedOffers.map(o => {
            return (
              <Grid container className="invoice-detail-modal__body__summary__item">
                <Grid item xs={8} className="invoice-detail-modal__body__summary__item__title">
                  {o.title}
                </Grid>
                <Grid item xs={4}>
                  {values.selectedPeriods
                    .filter(p => p.offerId === o.id)
                    .map(p => (
                      <Grid container className="invoice-detail-modal__body__summary__item__period">
                        <Grid
                          item
                          md
                          className="invoice-detail-modal__body__summary__item__period__id"
                        >
                          {p.id}
                        </Grid>
                        <Grid
                          item
                          md="auto"
                          className="invoice-detail-modal__body__summary__item__period__price"
                        >
                          {p.price}€
                        </Grid>
                      </Grid>
                    ))}
                </Grid>
              </Grid>
            );
          })}
        </Grid>
        <Grid container justifyContent="flex-end">
          {amount}€
        </Grid>
        <Grid container className="invoice-detail-modal__body__input__tax">
          <Grid item xs>
            <Grid container justifyContent="flex-end">
              <FormControlLabel
                value="start"
                control={
                  <Field
                    component={Switch}
                    name="invoiceSettings.tax"
                    color="primary"
                    checked={values.invoiceSettings.tax}
                    onChange={() =>
                      setFieldValue('invoiceSettings.tax', !values.invoiceSettings.tax)
                    }
                  />
                }
                label="MwSt."
                labelPlacement="end"
              />
            </Grid>
          </Grid>
          <Grid item xs={1} className="invoice-detail-modal__body__input__tax__amount">
            {values.invoiceSettings.tax ? `${taxAmount.toFixed(2).replace('.', ',')}€` : '0€'}
          </Grid>
        </Grid>
        <Grid container justifyContent="flex-end">
          <FormControl>
            <TextField
              className="invoice-detail-modal__body__input__amount"
              disabled
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon
                      className="fas fa-euro-sign"
                      style={{
                        color: 'green',
                        width: 'auto',
                        marginLeft: '0.5em'
                      }}
                    />
                  </InputAdornment>
                )
              }}
              value={`${finalAmount.toFixed(2).toString().replace('.', ',')}`}
            />
          </FormControl>
        </Grid>
      </Grid>
    </Grid>
  );
}
